import { FunctionComponent } from 'react';
import styled, { keyframes } from 'styled-components';
import type { DisplayStatus } from 'src/types/job';

const STATUS_STYLES: Record<DisplayStatus, { color: string; bg: string; border: string }> = {
    Queued:        { color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
    Processing:    { color: '#2563EB', bg: '#DBEAFE', border: '#BFDBFE' },
    Completed:     { color: '#059669', bg: '#D1FAE5', border: '#A7F3D0' },
    Failed:        { color: '#DC2626', bg: '#FEE2E2', border: '#FECACA' },
    'Needs Review': { color: '#D97706', bg: '#FEF3C7', border: '#FDE68A' },
    Stuck:         { color: '#DC2626', bg: '#FEF3C7', border: '#FDE68A' },
};

const Badge = styled.span<{ $color: string; $bg: string; $border: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    color: ${p => p.$color};
    background: ${p => p.$bg};
    border: 1px solid ${p => p.$border};
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
`;

const Dot = styled.span<{ $color: string; $animate?: boolean }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => p.$color};
    animation: ${p => p.$animate ? pulse : 'none'} 1.5s ease-in-out infinite;
`;

interface Props {
    status: DisplayStatus;
    className?: string;
}

const StatusBadge: FunctionComponent<Props> = ({ status, className }) => {
    const style = STATUS_STYLES[status];
    const animated = status === 'Processing';
    return (
        <Badge $color={style.color} $bg={style.bg} $border={style.border} className={className}>
            <Dot $color={style.color} $animate={animated} />
            {status}
        </Badge>
    );
};

export default StatusBadge;
